import { Button, Group, Modal, Stack, Textarea, TextInput } from '@mantine/core'
import { useState } from 'react'
import type { FavoriteEntry } from '../useFavorites'

const NAME_MAX = 50

type EditFavoriteModalProps = {
  opened: boolean
  favorite: FavoriteEntry | null
  onClose: () => void
  // Always called with a string `name` (possibly empty). Empty means "clear
  // the stored label"; the parent forwards an explicit empty string so the
  // server can distinguish "clear" from "field absent / preserve".
  onSave: (originalText: string, text: string, name: string) => void
}

export const EditFavoriteModal = ({
  opened,
  favorite,
  onClose,
  onSave,
}: EditFavoriteModalProps) => {
  // Initialize directly from the favorite prop so the first render already
  // has the right values (no flash of empty fields during the modal-open
  // animation). When the parent swaps in a different favorite while the
  // modal is mounted, we detect that during render and reset state — the
  // React-recommended "reset all state when a prop changes" pattern.
  const [activeText, setActiveText] = useState<string | null>(favorite?.text ?? null)
  const [name, setName] = useState(favorite?.name ?? '')
  const [text, setText] = useState(favorite?.text ?? '')

  if (favorite && favorite.text !== activeText) {
    setActiveText(favorite.text)
    setName(favorite.name ?? '')
    setText(favorite.text)
  }

  if (!favorite) return null

  const trimmedText = text.trim()
  const trimmedName = name.trim()
  const initialName = favorite.name ?? ''
  const isUnchanged = trimmedText === favorite.text && trimmedName === initialName
  const canSave = trimmedText.length > 0 && !isUnchanged

  const handleSave = () => {
    if (!canSave) return
    // Forward the trimmed name verbatim (possibly empty). The parent and
    // server treat '' as an explicit clear and a non-empty value as the
    // new label.
    onSave(favorite.text, trimmedText, trimmedName)
    onClose()
  }

  // Counter is keyed off the trimmed length so the hint matches what
  // actually gets persisted — leading/trailing whitespace doesn't count.
  const remaining = NAME_MAX - trimmedName.length

  return (
    <Modal opened={opened} onClose={onClose} title="Edit bookmark" size="lg">
      <Stack>
        <TextInput
          data-autofocus
          label="Name"
          placeholder="Short label (optional)"
          value={name}
          maxLength={NAME_MAX}
          onChange={(event) => setName(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              handleSave()
            }
          }}
          inputWrapperOrder={['label', 'input', 'description', 'error']}
          description={`${remaining} character${remaining === 1 ? '' : 's'} remaining`}
          styles={
            remaining === 0 ? { description: { color: 'var(--mantine-color-red-6)' } } : undefined
          }
        />
        <Textarea
          label="Question"
          value={text}
          onChange={(event) => setText(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault()
              handleSave()
            }
          }}
          autosize
          minRows={4}
          maxRows={12}
          required
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
